import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Btn, Card } from '@/components/ui-ios';
import { Megaphone, X, ChevronRight, ChevronLeft, Info, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';

const TONE_STYLES = {
  info: { bg: 'from-blue-500 to-blue-700', icon: Info, label: 'Annuncio' },
  success: { bg: 'from-emerald-500 to-emerald-700', icon: CheckCircle2, label: 'Novità' },
  warning: { bg: 'from-amber-500 to-orange-600', icon: AlertTriangle, label: 'Avviso' },
};

// Quanto spesso ricontrollare /announcements/pending mentre l'utente è
// già loggato. Tenere alto (>= 60s) per non sovraccaricare il backend
// con 100+ utenti connessi in contemporanea.
// 120s = compromesso fra "immediato" e "scalabile" — gli annunci non
// sono critici real-time, un ritardo max di 2 min va benissimo.
const POLL_INTERVAL_MS = 120_000;

/**
 * AnnouncementModal
 * Polls /api/announcements/pending after login AND periodically while
 * the user stays logged in, so SuperAdmin announcements appear within
 * ~2 minutes (no need to log out/in). Polling pauses automatically when
 * the browser tab is hidden, and resumes (with an immediate fetch) when
 * the tab regains focus.
 */
export default function AnnouncementModal() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  // Lock per evitare race-condition: se un poll arriva mentre l'utente
  // sta interagendo col modale, lo ignoriamo (l'aggiorniamo dopo la dismiss).
  const openRef = useRef(false);
  useEffect(() => { openRef.current = open; }, [open]);

  const fetchPending = useCallback(async ({ force = false } = {}) => {
    // Se il modale è già aperto e non è un force-refresh, non disturbiamo:
    // i nuovi annunci saranno presi al prossimo poll dopo il dismiss.
    if (openRef.current && !force) return;
    try {
      const { data } = await api.get('/announcements/pending');
      if (Array.isArray(data) && data.length > 0) {
        setQueue(data);
        setIdx(0);
        setOpen(true);
      }
    } catch (e) { /* silent: connessione persa o backend down → ritento al prossimo tick */ }
  }, []);

  useEffect(() => {
    if (!user) {
      setOpen(false);
      setQueue([]);
      return;
    }
    // 1) Primo fetch poco dopo il login (lascia il tempo all'app di montarsi).
    const initialTimer = setTimeout(() => fetchPending(), 400);

    // 2) Polling periodico. Saltato se la tab è hidden (risparmio CPU/rete).
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchPending();
    };
    const interval = setInterval(tick, POLL_INTERVAL_MS);

    // 3) Quando la tab torna in focus, fetcha SUBITO (così se l'utente
    //    riapre il PC alle 9 vede l'annuncio emesso alle 8:30).
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchPending();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, fetchPending]);

  const current = queue[idx];

  const handleDismiss = async () => {
    if (!current) return;
    setDismissing(true);
    try {
      await api.post(`/announcements/${current.id}/dismiss`);
    } catch (e) { /* ignore */ }
    setDismissing(false);
    if (idx + 1 < queue.length) {
      setIdx(idx + 1);
    } else {
      setOpen(false);
      setQueue([]);
      // Subito dopo aver chiuso l'ultimo annuncio della coda, ricontrolla
      // se nel frattempo SA ne ha pubblicati altri → li mostra a catena
      // senza attendere il prossimo tick di polling.
      setTimeout(() => fetchPending({ force: true }), 250);
    }
  };

  if (!open || !current) return null;

  const tone = TONE_STYLES[current.tone || 'info'] || TONE_STYLES.info;
  const Icon = tone.icon;
  const hasMultiple = queue.length > 1;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm animate-in fade-in"
      data-testid="announcement-modal"
      role="dialog"
      aria-modal="true"
    >
      <Card className="w-full max-w-lg overflow-hidden p-0 shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-3 duration-200">
        {/* Header banner */}
        <div className={`relative bg-gradient-to-br ${tone.bg} text-white p-6 pr-12`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
              <Megaphone className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider opacity-90 font-semibold">{tone.label}</div>
              <h3 className="text-lg font-bold leading-tight" data-testid="announcement-title">{current.title}</h3>
            </div>
          </div>
          {/* Index indicator */}
          {hasMultiple && (
            <div className="flex items-center gap-1.5 mt-2" data-testid="announcement-pager">
              {queue.map((item, i) => (
                <span key={item?.id || `dot-${i}`} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-white' : 'w-1.5 bg-white/40'}`} />
              ))}
              <span className="ml-2 text-xs opacity-90">{idx + 1} di {queue.length}</span>
            </div>
          )}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 inline-flex items-center justify-center transition-colors"
            aria-label="Chiudi"
            data-testid="announcement-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {current.image_url && (
            <img
              src={current.image_url}
              alt=""
              className="w-full max-h-56 object-cover rounded-2xl border border-[color:var(--hairline)]"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div className="flex items-start gap-2.5">
            <Icon className="h-4 w-4 mt-0.5 text-[color:var(--text-secondary)] shrink-0" />
            <p className="text-sm leading-relaxed whitespace-pre-wrap text-zinc-800" data-testid="announcement-body">
              {current.body}
            </p>
          </div>
          {current.link_url && (
            <a
              href={current.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-orange)] hover:underline"
              data-testid="announcement-link"
            >
              {current.link_label || 'Scopri di più'} <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-1 flex items-center justify-between gap-3 border-t border-[color:var(--hairline)] bg-[color:var(--surface-2)]">
          <div className="text-[11px] text-[color:var(--text-secondary)]">
            {current.created_by_name ? `Da ${current.created_by_name}` : 'PostaCentrale'}
          </div>
          <Btn variant="accent" onClick={handleDismiss} disabled={dismissing} testid="announcement-dismiss">
            {idx + 1 < queue.length ? <>Successivo <ChevronRight className="h-4 w-4" /></> : 'Ho letto'}
          </Btn>
        </div>
      </Card>
    </div>
  );
}
